import React, { useRef, useState, useLayoutEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { AiGeneratedChip, OutlinedIcons, RichTextDisplay } from '@marageti/z4-lib';
import { TravelerProfile } from '@marageti/z4-sdk/lib/people';
import EditTravelPreferencesModal from './edit-travel-preferences-modal';
import { CATEGORIES, CategoryConfig } from './utils';
import SuggestionSlot from '../traveler-profile-suggestions/suggestion-slot';
import SuggestionSkeleton from '../traveler-profile-suggestions/suggestion-skeleton';
import DismissedSuggestionRow from '../traveler-profile-suggestions/dismissed-suggestion-row';
import { useTravelPreferenceSuggestionPreview } from '../../hooks/use-traveler-profile-suggestion-preview';

const { OutlinedDocumentDuplicateIcon } = OutlinedIcons;

const COLLAPSED_MAX_HEIGHT = 144;

type PreferenceSectionProps = {
  canEdit: boolean;
  label: string;
  value: string | undefined;
  onEditClick: () => void;
  /**
   * When set, renders an "Undo" button in place of the Edit button. Used to
   * back out an accepted but not-yet-saved AI suggestion preview.
   */
  onUndoClick?: () => void;
};

const PreferenceSection = ({
  canEdit, label, value, onEditClick, onUndoClick,
}: PreferenceSectionProps) => {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (contentRef.current) {
      setOverflows(contentRef.current.scrollHeight > COLLAPSED_MAX_HEIGHT);
    }
  }, [value]);

  return (
    <Stack spacing={0.5}>
      <Typography variant="subtitle2">
        {label}
      </Typography>
      <Box
        ref={contentRef}
        sx={{
          maxHeight: expanded ? 'none' : `${COLLAPSED_MAX_HEIGHT}px`,
          overflow: 'hidden',
          position: 'relative',
          color: 'text.secondary',
        }}
      >
        {value ? (
          <RichTextDisplay>{value}</RichTextDisplay>
        ) : (
          <Typography variant="body" color="common.black50">
            No preferences recorded
          </Typography>
        )}
      </Box>
      {value && overflows && (
        <Button
          size="small"
          variant="text"
          onClick={() => setExpanded((prev) => !prev)}
          sx={{
            alignSelf: 'flex-start', px: 0, minWidth: 0, fontWeight: 'normal', fontSize: '16px', color: 'text.secondary',
          }}
        >
          {expanded ? 'Show Less' : 'Show More'}
        </Button>
      )}
      {onUndoClick ? (
        <Button
          size="small"
          variant="text"
          onClick={onUndoClick}
          sx={{
            alignSelf: 'flex-start', px: 0, minWidth: 0, typography: 'captionBold', color: 'primary.main',
          }}
        >
          Undo
        </Button>
      ) : canEdit && (
        <Button
          size="small"
          variant="text"
          onClick={onEditClick}
          sx={{
            alignSelf: 'flex-start', px: 0, minWidth: 0, typography: 'captionBold', color: 'primary.main',
          }}
        >
          {value ? 'Edit Preferences' : '+ Add Preferences'}
        </Button>
      )}
    </Stack>
  );
};

type PreferenceRowProps = {
  category: CategoryConfig;
  canEdit: boolean;
  value: string | undefined;
  onEdit: () => void;
};

const PreferenceRow = ({
  category, canEdit, value, onEdit,
}: PreferenceRowProps) => {
  const preview = useTravelPreferenceSuggestionPreview(category.key, {
    hasContent: !!value,
  });

  if (preview.state === 'pending') {
    return (
      <Stack spacing={0.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="subtitle2">{category.label}</Typography>
          <AiGeneratedChip />
        </Stack>
        <SuggestionSlot
          boxed
          showChip={false}
          disabled={preview.disabled}
          onApply={preview.apply}
          onDismiss={preview.dismiss}
        >
          <Box sx={{ color: 'text.secondary' }}>
            <RichTextDisplay>{preview.value}</RichTextDisplay>
          </Box>
        </SuggestionSlot>
      </Stack>
    );
  }

  if (preview.state === 'loading') {
    return (
      <Stack spacing={0.5}>
        <Typography variant="subtitle2">{category.label}</Typography>
        <SuggestionSkeleton lines={3} />
      </Stack>
    );
  }

  // Dismissed: keep the row's label in place with a recoverable "skipped"
  // marker so the user can change their mind during review.
  if (preview.state === 'dismissed') {
    return (
      <Stack spacing={0.5}>
        <Typography variant="subtitle2">{category.label}</Typography>
        <DismissedSuggestionRow onUndo={preview.undo} />
      </Stack>
    );
  }

  // Preview accepted suggestions in-place via the normal PreferenceSection
  // render path so the row looks populated between "Apply to Profile" and
  // the eventual save. Edit is replaced with Undo so the user can back out
  // the staged value without nuking the whole review.
  //
  // While the AI import review is in flight, lock sub-keys the AI didn't
  // return content for so the user can't half-edit other preferences mid-
  // review. Sub-keys with an in-flight suggestion are handled by the
  // branches above; only the idle/accepted fall-through reaches here.
  const acceptedPreview = preview.state === 'accepted' ? preview.value : undefined;
  const lockedForReview = preview.state === 'idle' && preview.lockedForReview;

  return (
    <PreferenceSection
      canEdit={acceptedPreview || lockedForReview ? false : canEdit}
      label={category.label}
      value={acceptedPreview ?? value}
      onEditClick={onEdit}
      onUndoClick={preview.state === 'accepted' ? preview.undo : undefined}
    />
  );
};

type TravelPreferencesCardViewProps = {
  travelerProfile: TravelerProfile;
  canEdit: boolean;
  copied: boolean;
  openCategory: CategoryConfig | null;
  onCopy: () => void;
  onEditCategory: (category: CategoryConfig) => void;
  onCloseModal: () => void;
  onModalSave: (value: string) => Promise<void>;
};

const TravelPreferencesCardView = ({
  travelerProfile,
  canEdit,
  copied,
  openCategory,
  onCopy,
  onEditCategory,
  onCloseModal,
  onModalSave,
}: TravelPreferencesCardViewProps) => (
  <>
    <Card>
      <CardHeader
        title="Travel Preferences"
        titleTypographyProps={{ variant: 'h3' }}
        action={(
          <Tooltip title={copied ? 'Copied!' : 'Copy all preferences'}>
            <IconButton size="small" onClick={onCopy}>
              {copied
                ? <CheckIcon fontSize="small" color="success" />
                : <OutlinedDocumentDuplicateIcon fontSize="small" sx={{ color: 'text.primary' }} />}
            </IconButton>
          </Tooltip>
        )}
      />
      <CardContent>
        <Stack divider={<Divider />} spacing={2}>
          {CATEGORIES.map((category) => (
            <PreferenceRow
              key={String(category.key)}
              category={category}
              canEdit={canEdit}
              value={travelerProfile.travelPreferences?.[category.key] as string | undefined}
              onEdit={() => onEditCategory(category)}
            />
          ))}
        </Stack>
      </CardContent>
    </Card>

    {openCategory && (
      <EditTravelPreferencesModal
        open={openCategory !== null}
        onClose={onCloseModal}
        categoryKey={openCategory.key}
        categoryLabel={openCategory.label}
        currentValue={travelerProfile.travelPreferences?.[openCategory.key] as string | undefined}
        defaultAsList={openCategory.defaultAsList ?? true}
        onSave={onModalSave}
      />
    )}
  </>
);

export default TravelPreferencesCardView;
